'use client'

import React, { FC, PropsWithChildren, useMemo } from 'react'
import { notFound, redirect, usePathname } from 'next/navigation'
import { useFlags } from 'launchdarkly-react-client-sdk'

import {
  getCurrentPage,
  getOperatorHomePagePath,
  getOperatorPages,
} from '~/app/helpers'

import { User } from '~/hooks/useAuthentication/types'

import DesktopLayout from '~/app/(authenticated)/DesktopLayout'
import MobileLayout from '~/app/(authenticated)/MobileLayout'
import OperatorNewDesktopLayout from '~/app/(authenticated)/OperatorNewDesktopLayout'

type Props = {
  user: User
  device: { isDesktop: boolean }
  onAction?: (actionKey: string) => void
}

const OperatorAuthenticatedShell: FC<PropsWithChildren<Props>> = ({
  children,
  user,
  device,
  onAction,
}) => {
  const pathname = usePathname()
  const flag = useFlags()

  if (!user) {
    notFound()
  }

  const availablePages = useMemo(() => {
    return getOperatorPages(user?.role).filter((page) => {
      return !page.featureFlag || flag[page.featureFlag]
    })
  }, [flag, user?.role])

  if (flag['allowNewUserWorkflows'] && device.isDesktop) {
    return (
      <OperatorNewDesktopLayout
        pathName={pathname}
        userRole={user.role}
        onAction={onAction}
      >
        {children}
      </OperatorNewDesktopLayout>
    )
  }
  const currentPage = getCurrentPage(pathname, availablePages)

  if (!currentPage) {
    if (flag['showHomePageSetting']) {
      redirect(getOperatorHomePagePath(user.settings.homePage))
    } else {
      redirect('/visibility')
    }
  }
  const LayoutComponent = device.isDesktop ? DesktopLayout : MobileLayout

  return (
    <LayoutComponent
      availablePages={availablePages}
      currentPage={currentPage}
      user={user}
      onOpenSettings={() => onAction?.('settings')}
    >
      {children}
    </LayoutComponent>
  )
}

export default OperatorAuthenticatedShell
